import {
  Box,
  Grid,
  HStack,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack
} from '@chakra-ui/react';
import React, { FC, useMemo } from 'react';

import s from '@app/components/Swap/components/TokenSelector/TokenSelector.module.scss';
import { useRouterIntentState } from '@app/state/routerIntentStore';
import {
  Arbitrum,
  Ethereum,
  Linea,
  MantaPacific,
  Mantle,
  Polygon
} from '@thirdweb-dev/chains';
import { Token } from '@app/types/token';
import { NATIVE_TOKEN_ADDRESS } from '@thirdweb-dev/sdk';
import { TokenIcon } from '@app/components/TokenIcon';
import Link from 'next/link';
import { formatWalletAddress } from '@app/helpers/format';
import { CopyButton } from '@app/components/CopyButton';

interface Props {
  onClose: () => void;
  isOpen: boolean;
}

const TOKENS: Record<string, Token[]> = {
  [Arbitrum.chainId]: [
    {
      contractAddress: NATIVE_TOKEN_ADDRESS,
      symbol: 'ETH',
      decimals: 18,
      name: 'ETH',
      logoUri: ''
    },
    {
      contractAddress: '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9',
      symbol: 'USDT',
      decimals: 6,
      name: 'Tether USD',
      logoUri: ''
    },
    {
      contractAddress: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831',
      symbol: 'USDC',
      decimals: 6,
      name: 'USD Coin',
      logoUri: ''
    }
  ],
  [Polygon.chainId]: [
    {
      contractAddress: NATIVE_TOKEN_ADDRESS,
      symbol: 'ETH',
      decimals: 18,
      name: 'ETH',
      logoUri: ''
    },
    {
      contractAddress: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F',
      symbol: 'USDT',
      decimals: 6,
      name: 'Tether USD',
      logoUri: ''
    },
    {
      contractAddress: '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359',
      symbol: 'USDC',
      decimals: 6,
      name: 'USD Coin',
      logoUri: ''
    }
  ],
  [Ethereum.chainId]: [
    {
      contractAddress: NATIVE_TOKEN_ADDRESS,
      symbol: 'ETH',
      decimals: 18,
      name: 'ETH',
      logoUri: ''
    },
    {
      contractAddress: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
      symbol: 'USDC',
      decimals: 6,
      name: 'USD Coin',
      logoUri: ''
    },
    {
      contractAddress: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
      symbol: 'USDT',
      decimals: 6,
      name: 'Tether USD',
      logoUri: ''
    },
    {
      contractAddress: '0xbf5495Efe5DB9ce00f80364C8B423567e58d2110',
      symbol: 'ezETH',
      decimals: 18,
      name: 'Renzo Staked ETH',
      logoUri: ''
    },
    {
      contractAddress: '0xCd5fE23C85820F7B72D0926FC9b05b43E359b7ee',
      symbol: 'weETH',
      decimals: 18,
      name: 'Wrapped eETH',
      logoUri: ''
    },
    {
      contractAddress: '0xA1290d69c65A6Fe4DF752f95823fae25cB99e5A7',
      symbol: 'wrsETH',
      decimals: 18,
      name: 'wrsETH',
      logoUri: ''
    },
    {
      contractAddress: '0xd5F7838F5C461fefF7FE49ea5ebaF7728bB0ADfa',
      symbol: 'mETH',
      decimals: 18,
      name: 'mETH',
      logoUri: ''
    },
    {
      contractAddress: '0x7122985656e38BDC0302Db86685bb972b145bD3C',
      symbol: 'STONE',
      decimals: 18,
      name: 'STONE',
      logoUri: ''
    },
    {
      contractAddress: '0xfa2629B9cF3998D52726994E0FcdB750224D8B9D',
      symbol: 'inETH',
      decimals: 18,
      name: 'inETH',
      logoUri: ''
    },
    {
      contractAddress: '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599',
      symbol: 'wBTC',
      decimals: 8,
      name: 'wBTC',
      logoUri: ''
    }
  ],
  [Linea.chainId]: [
    {
      contractAddress: NATIVE_TOKEN_ADDRESS,
      symbol: 'ETH',
      decimals: 18,
      name: 'ETH',
      logoUri: ''
    },
    {
      contractAddress: '0xA219439258ca9da29E9Cc4cE5596924745e12B93',
      symbol: 'USDT',
      decimals: 6,
      name: 'Tether USD',
      logoUri: ''
    },
    {
      contractAddress: '0x176211869cA2b568f2A7D4EE941E073a821EE1ff',
      symbol: 'USDC',
      decimals: 6,
      name: 'USD Coin',
      logoUri: ''
    }
  ],
  [Mantle.chainId]: [
    {
      contractAddress: NATIVE_TOKEN_ADDRESS,
      symbol: 'ETH',
      decimals: 18,
      name: 'ETH',
      logoUri: ''
    }
  ],
  [MantaPacific.chainId]: [
    {
      contractAddress: NATIVE_TOKEN_ADDRESS,
      symbol: 'ETH',
      decimals: 18,
      name: 'ETH',
      logoUri: ''
    }
  ]
};

export const SelectTokenModal: FC<Props> = ({ onClose, isOpen }) => {
  const {
    sourceNetwork,
    actions: { setSourceToken }
  } = useRouterIntentState();

  const tokenOptions = useMemo(() => {
    if (!sourceNetwork) {
      return [];
    }

    return TOKENS[sourceNetwork.chainId.toString()];
  }, [sourceNetwork]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
      }}
      isCentered
      motionPreset="slideInBottom"
    >
      <ModalOverlay />
      <ModalContent sx={{ maxWidth: ['100vw', '600px'] }}>
        <ModalHeader>
          <HStack justify="space-between" px={0} py={0} mb="16px">
            <Text fontSize="16px" color="white" fontWeight="500">
              Select token
            </Text>
            <ModalCloseButton
              onClick={() => {
                onClose();
              }}
            />
          </HStack>
        </ModalHeader>

        <VStack>
          <Box
            className={s.scrollWrapper}
            overflowY="auto"
            height={400}
            mt={4}
            w="100%"
          >
            {tokenOptions?.map(item => {
              return (
                <Grid
                  onClick={() => {
                    setSourceToken(item);
                    onClose();
                  }}
                  style={{
                    cursor: 'pointer',
                    transition: 'background 0.2s ease-out'
                  }}
                  key={item.contractAddress}
                  gridTemplateColumns="48px 1fr 100px"
                  gridColumnGap="8px"
                  gridRowGap={0}
                  borderBottom="1px solid"
                  borderColor="whiteOpacity.6"
                  alignItems="center"
                  _hover={{ background: 'neutral.700' }}
                  w="100%"
                  gridTemplateAreas="'icon source details' 'icon symbol details'"
                  py="12px"
                  px="12px"
                >
                  <TokenIcon
                    symbol={item?.symbol}
                    iconUrl={item?.logoUri}
                    gridArea="icon"
                  />
                  <Text
                    fontSize="16px"
                    lineHeight="21px"
                    fontWeight={500}
                    color="neutral.100"
                    gridArea="source"
                    textAlign="left"
                  >
                    {item.name}
                  </Text>
                  <HStack gridArea="symbol">
                    <Text
                      fontSize="14px"
                      color="neutral.400"
                      // textTransform="capitalize"
                      textAlign="left"
                    >
                      {item.symbol}
                    </Text>
                    <Box width="1px" height="20px" bg="neutral.500" />
                    <Link
                      onClick={e => e.stopPropagation()}
                      href={`${sourceNetwork?.explorers?.[0]?.url}/address/${item.contractAddress}`}
                      target="_blank"
                    >
                      <Text
                        fontSize="14px"
                        color="neutral.400"
                        // textTransform="capitalize"
                        textAlign="left"
                        _hover={{ color: 'neutral.100' }}
                      >
                        {formatWalletAddress(item.contractAddress)}
                      </Text>
                    </Link>
                    <CopyButton text={item.contractAddress} />
                  </HStack>
                </Grid>
              );
            })}
          </Box>
        </VStack>
      </ModalContent>
    </Modal>
  );
};
